// In-memory campaign store for mock mode. Holds CampaignState keyed by id and
// records fork provenance edges for the lineage view.

import type {
  CampaignConfig,
  CampaignState,
  SamplePoint,
  WebSocketEvent,
} from "../types";

export type ForkEdge = {
  fromId: string;
  toId: string;
  atIteration: number;
};

type Subscriber = (event: WebSocketEvent) => void;

class CampaignStore {
  private campaigns = new Map<string, CampaignState>();
  private subscribers = new Map<string, Set<Subscriber>>();
  public forks: ForkEdge[] = [];

  has(id: string): boolean {
    return this.campaigns.has(id);
  }

  get(id: string): CampaignState | undefined {
    return this.campaigns.get(id);
  }

  all(): CampaignState[] {
    return Array.from(this.campaigns.values());
  }

  set(state: CampaignState): void {
    state.updatedAt = new Date().toISOString();
    this.campaigns.set(state.id, state);
  }

  delete(id: string): void {
    this.campaigns.delete(id);
    this.subscribers.delete(id);
  }

  // Create an empty metrics object scaffolded from the output list.
  static emptyMetrics(config: CampaignConfig) {
    const perOutput: CampaignState["metrics"]["perOutput"] = {};
    for (const o of config.outputs) {
      const entry = {
        maxSigmaByIter: [] as number[],
        meanSigmaByIter: [] as number[],
        currentMaxSigma: 0,
        currentMeanSigma: 0,
        targetSigma: o.targetSigma,
        calibration: [] as Array<{ predictedSigma: number; realisedAbsError: number }>,
      } as CampaignState["metrics"]["perOutput"][string];
      if (config.oracle?.enabled) {
        entry.realRmseByIter = [];
        entry.currentRealRmse = 0;
      }
      perOutput[o.name] = entry;
    }
    return {
      perOutput,
      cumulativeCostFlexCredits: 0,
      autonomousRunCount: 0,
      manualRunCount: 0,
    };
  }

  static newState(id: string, config: CampaignConfig): CampaignState {
    const now = new Date().toISOString();
    return {
      id,
      config,
      status: "configuring",
      iteration: 0,
      pointsComplete: 0,
      pointsInFlight: 0,
      points: [],
      metrics: CampaignStore.emptyMetrics(config),
      createdAt: now,
      updatedAt: now,
    };
  }

  recordFork(edge: ForkEdge): void {
    this.forks.push(edge);
  }

  // ---- Subscribe / emit ---------------------------------------------------

  subscribe(id: string, cb: Subscriber): () => void {
    let set = this.subscribers.get(id);
    if (!set) {
      set = new Set();
      this.subscribers.set(id, set);
    }
    set.add(cb);
    // Emit immediate state snapshot so new subscribers see current data.
    const state = this.campaigns.get(id);
    if (state) cb({ type: "state", campaignId: id, state });
    return () => {
      set?.delete(cb);
    };
  }

  emit(id: string, event: WebSocketEvent): void {
    const set = this.subscribers.get(id);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(event);
      } catch {
        // Silently swallow — subscriber bugs shouldn't break the simulator.
      }
    }
  }

  // For tests: reset everything.
  reset(): void {
    this.campaigns.clear();
    this.subscribers.clear();
    this.forks = [];
  }

  // Unique helpers to mint ids.
  private counters = { point: 0, batch: 0, manualBatch: 0 };

  nextPointId(): string {
    this.counters.point += 1;
    return `p_${String(this.counters.point).padStart(4, "0")}`;
  }

  nextBatchId(kind: "sampler" | "manual"): string {
    if (kind === "manual") {
      this.counters.manualBatch += 1;
      return `b_manual_${String(this.counters.manualBatch).padStart(4, "0")}`;
    }
    this.counters.batch += 1;
    return `b_${String(this.counters.batch).padStart(4, "0")}`;
  }
}

export const campaignStore = new CampaignStore();

export function generateCampaignId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `camp_${Date.now().toString(36)}_${rand}`;
}

export function cloneStateForFork(
  source: CampaignState,
  newId: string,
  atIteration: number,
  overrides: Partial<CampaignConfig> | undefined,
): CampaignState {
  const baseConfig: CampaignConfig = { ...source.config };
  const mergedConfig: CampaignConfig = overrides
    ? { ...baseConfig, ...overrides,
        // Deep-merge outputs/inputs/oracle if partial overrides provided.
        inputs: overrides.inputs ?? baseConfig.inputs,
        outputs: overrides.outputs ?? baseConfig.outputs,
        oracle: overrides.oracle ?? baseConfig.oracle }
    : baseConfig;

  const truncatedPoints: SamplePoint[] = source.points
    .filter((p) => p.iter <= atIteration)
    .map((p) => ({ ...p }));

  const now = new Date().toISOString();
  return {
    id: newId,
    config: mergedConfig,
    status: "configuring",
    iteration: atIteration,
    pointsComplete: truncatedPoints.filter((p) => p.status === "done").length,
    pointsInFlight: truncatedPoints.filter((p) => p.status === "running" || p.status === "queued").length,
    points: truncatedPoints,
    metrics: CampaignStore.emptyMetrics(mergedConfig),
    createdAt: now,
    updatedAt: now,
  };
}

export { CampaignStore };
